import { useEffect, useState } from "react";
import { toast } from "sonner";

import legalAdviceHeadset from "assets/svg/legal-advice-headset.svg";
import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";
import {
  OppositionWorkflowStep,
  getOppositionCounterByOpponent,
  getOppositionProgress,
  recordOppositionCallClientForEvidence,
  type CounterByOpponentItem,
  type OppositionProgressStep,
} from "api/opposition";

export const OPPOSITION_CALL_CLIENT_FOR_EVIDENCE_FORM_ID =
  "opposition-call-client-for-evidence-form";

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

export default function OppositionCallClientForEvidenceView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [marks, setMarks] = useState<CounterByOpponentItem[]>([]);
  const [recording, setRecording] = useState(false);
  const [recorded, setRecorded] = useState(false);

  useEffect(() => {
    if (!leadId) {
      setMarks([]);
      setRecorded(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionCounterByOpponent(leadId),
      getOppositionProgress(leadId).catch(() => null),
    ])
      .then(([data, progress]) => {
        if (cancelled) return;
        setMarks(data.conflictingMarks);
        if (progress) {
          const step = progress.steps.find(
            (s: OppositionProgressStep) =>
              s.slug ===
              OppositionWorkflowStep.OP_S3_04_CallClientForEvidenceByOpponent
          );
          setRecorded(Boolean(step?.isCompleted || step?.isSkipped));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : "Failed to load step data");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Only marks where Counter was filed (YES) are eligible — Evidence is only
  // required when the opponent has filed a Counter Statement.
  const eligible = marks.filter((m) => m.counterFiled === true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || recording) return;
    if (recorded) {
      onSuccess?.(leadId);
      return;
    }
    if (eligible.length === 0) {
      toast.error(
        "No applications are eligible. Mark Counter Filed = YES in the previous step first."
      );
      return;
    }
    setRecording(true);
    try {
      await recordOppositionCallClientForEvidence(leadId, {
        conflictingMarkIds: eligible.map((m) => m.id),
      });
      setRecorded(true);
      toast.success("Call Client step recorded successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to record call client step"
      );
    } finally {
      setRecording(false);
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  return (
    <form
      id={OPPOSITION_CALL_CLIENT_FOR_EVIDENCE_FORM_ID}
      onSubmit={(e) => void handleSubmit(e)}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {eligible.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {eligible.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] bg-[#222222] text-white"
              style={{ fontSize: "0.75rem" }}
            >
              <RadioCircle active />
              {formatTmNoForDisplay(m.applicationNo ?? "")}
            </div>
          ))}
        </div>
      )}

      <div
        className="flex flex-row items-center gap-4 p-4"
        style={{
          background: "#FFF8EE",
          border: "1.5px dashed #F5A623",
          borderRadius: "12px",
        }}
      >
        <img
          src={legalAdviceHeadset}
          alt=""
          className="w-[60px] h-[60px] flex-shrink-0"
        />

        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <p className="text-sm font-normal text-[#222222]" style={{ lineHeight: "20px" }}>
            Call the client to explain the need to file evidence in support of
            the opposition and confirm whether they wish to go ahead.
          </p>

          {recorded ? (
            <div className="flex items-center gap-2">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <circle cx="8" cy="8" r="7.5" fill="#222222" />
                <path
                  d="M5.33325 8.00008L7.33325 10.0001L10.6666 6.00008"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span
                className="text-xs font-semibold text-[#222222]"
                style={{ lineHeight: "20px" }}
              >
                Call Completed
              </span>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => void handleSubmit(e as unknown as React.FormEvent)}
              disabled={recording || eligible.length === 0}
              className="self-start h-8 px-4 text-xs font-medium text-[#222222] bg-white border border-[#D1D5DB] rounded-md hover:bg-[#F9FAFB] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              style={{ lineHeight: "20px" }}
            >
              {recording ? "Recording…" : "Call Completed"}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
